// src/chartUtils.ts
import * as d3 from 'd3';
import {
  TimeSeriesDataPoint,
  EVHistoryEntry,
  LiquidityHistoryEntry,
  BackOddsHistoryEntry,
  D3ChartConfig,
} from './types';

/**
 * Processes raw time series data for charting by filtering, sorting, removing duplicates,
 * and aggregating based on time span.
 * @param rawData - Array of raw time series data points.
 * @returns An object containing the processed data and the determined time scale.
 */
export const processTimeSeriesData = (
  rawData: TimeSeriesDataPoint[]
): { data: TimeSeriesDataPoint[]; timeScale: string } => {
  try {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return { data: [], timeScale: 'minute' };
    }

    const data = [...rawData].filter(
      (item): item is TimeSeriesDataPoint =>
        (item &&
          item.timestamp &&
          'layEV' in item &&
          typeof item.layEV !== 'undefined') ||
        ('midpointEV' in item && typeof item.midpointEV !== 'undefined') ||
        ('total' in item && typeof item.total !== 'undefined') ||
        ('adjustedEV' in item && typeof item.adjustedEV !== 'undefined')
    );

    if (data.length === 0) {
      return { data: [], timeScale: 'minute' };
    }

    data.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    const uniqueData: TimeSeriesDataPoint[] = [];
    for (let i = 0; i < data.length; i++) {
      const current = data[i];
      const previous = i > 0 ? data[i - 1] : null;
      if (!previous) {
        uniqueData.push(current);
        continue;
      }
      const timeDiff = Math.abs(
        new Date(current.timestamp).getTime() -
          new Date(previous.timestamp).getTime()
      );

      if (
        'layEV' in current &&
        'layEV' in previous &&
        typeof current.layEV !== 'undefined' // Check if current.layEV is defined
      ) {
        const currentEv = current as EVHistoryEntry;
        const prevEv = previous as EVHistoryEntry;
        if (timeDiff < 2000) {
          const layEVDiff = Math.abs(
            (currentEv.layEV ?? 0) - (prevEv.layEV ?? 0)
          );
          const midpointEVDiff = Math.abs(
            (currentEv.midpointEV ?? 0) - (prevEv.midpointEV ?? 0)
          );
          if (layEVDiff < 0.5 && midpointEVDiff < 0.5) continue;
        }
      } else if (
        'total' in current &&
        'total' in previous &&
        typeof current.total !== 'undefined' && // Check if current.total is defined
        timeDiff < 1000 &&
        current.total === (previous as LiquidityHistoryEntry).total
      ) {
        continue;
      } else if (
        'adjustedEV' in current &&
        'adjustedEV' in previous &&
        typeof current.adjustedEV !== 'undefined' && // Check if current.adjustedEV is defined
        timeDiff < 1000 &&
        current.adjustedEV === (previous as BackOddsHistoryEntry).adjustedEV
      ) {
        continue;
      }
      uniqueData.push(current);
    }

    if (uniqueData.length === 0) {
      return { data: [], timeScale: 'minute' };
    }

    const firstTimestamp = new Date(uniqueData[0].timestamp);
    if (isNaN(firstTimestamp.getTime())) {
      return { data: uniqueData, timeScale: 'minute' };
    }

    const timeSpanMinutes =
      (new Date().getTime() - firstTimestamp.getTime()) / (1000 * 60);
    let timeScale: string;
    let bucketSizeMinutes: number;

    if (timeSpanMinutes < 10) {
      timeScale = 'minute';
      bucketSizeMinutes = 1;
    } else if (timeSpanMinutes < 30) {
      timeScale = '2minute';
      bucketSizeMinutes = 2;
    } else if (timeSpanMinutes < 60) {
      timeScale = '5minute';
      bucketSizeMinutes = 5;
    } else if (timeSpanMinutes < 120) {
      timeScale = '10minute';
      bucketSizeMinutes = 10;
    } else if (timeSpanMinutes < 240) {
      timeScale = '15minute';
      bucketSizeMinutes = 15;
    } else if (timeSpanMinutes < 480) {
      timeScale = '30minute';
      bucketSizeMinutes = 30;
    } else if (timeSpanMinutes < 1440) {
      timeScale = 'hour';
      bucketSizeMinutes = 60;
    } else if (timeSpanMinutes < 5760) {
      timeScale = '4hour';
      bucketSizeMinutes = 240;
    } else if (timeSpanMinutes < 11520) {
      timeScale = '8hour';
      bucketSizeMinutes = 480;
    } else if (timeSpanMinutes < 17280) {
      timeScale = '12hour';
      bucketSizeMinutes = 720;
    } else {
      timeScale = 'day';
      bucketSizeMinutes = 1440;
    }

    const aggregatedData: TimeSeriesDataPoint[] = [];
    if (bucketSizeMinutes > 1 && uniqueData.length > 0) {
      const buckets: Record<number, any> = {};
      uniqueData.forEach((point) => {
        const pointTime = new Date(point.timestamp);
        const bucketTime = new Date(pointTime);
        bucketTime.setMinutes(
          Math.floor(pointTime.getMinutes() / bucketSizeMinutes) *
            bucketSizeMinutes
        );
        bucketTime.setSeconds(0);
        bucketTime.setMilliseconds(0);
        const bucketKey = bucketTime.getTime();
        if (!buckets[bucketKey]) {
          buckets[bucketKey] = {
            timestamp: bucketTime,
            layEVSum: 0,
            layEVCount: 0,
            midpointEVSum: 0,
            midpointEVCount: 0,
            totalSum: 0,
            totalCount: 0,
            adjustedEVSum: 0,
            adjustedEVCount: 0,
            ratingSum: 0,
            ratingCount: 0,
          };
        }
        if ('layEV' in point && point.layEV !== null) {
          buckets[bucketKey].layEVSum += point.layEV;
          buckets[bucketKey].layEVCount++;
        }
        if ('midpointEV' in point && point.midpointEV !== null) {
          buckets[bucketKey].midpointEVSum += point.midpointEV;
          buckets[bucketKey].midpointEVCount++;
        }
        if ('total' in point && point.total !== null) {
          buckets[bucketKey].totalSum += point.total;
          buckets[bucketKey].totalCount++;
          if (
            'rating' in point &&
            (point as LiquidityHistoryEntry).rating !== null
          ) {
            buckets[bucketKey].ratingSum += (
              point as LiquidityHistoryEntry
            ).rating;
            buckets[bucketKey].ratingCount++;
          }
        }
        if ('adjustedEV' in point && point.adjustedEV !== null) {
          buckets[bucketKey].adjustedEVSum += point.adjustedEV;
          buckets[bucketKey].adjustedEVCount++;
        }
      });
      Object.values(buckets).forEach((bucket) => {
        const aggPoint: Partial<
          EVHistoryEntry & LiquidityHistoryEntry & BackOddsHistoryEntry
        > & { timestamp: Date } = { timestamp: bucket.timestamp };
        if (bucket.layEVCount > 0)
          aggPoint.layEV = Math.round(bucket.layEVSum / bucket.layEVCount);
        else aggPoint.layEV = null;
        if (bucket.midpointEVCount > 0)
          aggPoint.midpointEV = Math.round(
            bucket.midpointEVSum / bucket.midpointEVCount
          );
        else aggPoint.midpointEV = null;
        if (bucket.totalCount > 0)
          aggPoint.total = Math.round(bucket.totalSum / bucket.totalCount);
        if (bucket.ratingCount > 0)
          (aggPoint as LiquidityHistoryEntry).rating = Math.round(
            bucket.ratingSum / bucket.ratingCount
          );
        if (bucket.adjustedEVCount > 0)
          aggPoint.adjustedEV = Math.round(
            bucket.adjustedEVSum / bucket.adjustedEVCount
          );
        else aggPoint.adjustedEV = null;
        aggregatedData.push(aggPoint as TimeSeriesDataPoint);
      });
      aggregatedData.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      return { data: aggregatedData, timeScale };
    }
    return { data: uniqueData, timeScale };
  } catch (error) {
    console.error('Error processing time series data:', error);
    return { data: [], timeScale: 'minute' };
  }
};

/**
 * Renders a generic D3 time series chart.
 * @param svgElement - The SVG DOM element to render the chart in.
 * @param data - The array of data points for the chart.
 * @param config - Configuration for the chart (colors, formatters, data key).
 * @param chartTimeScale - The time scale string (e.g., "minute", "5minute").
 */
/**
 * Helper function to convert CSS variable names to valid CSS class names
 */
const cssVarToClassName = (cssVar: string): string => {
  // Extract the variable name from var(--variable-name) format
  const match = cssVar.match(/var\(--([^)]+)\)/);
  if (match) {
    return match[1].replace(/-/g, '_'); // Replace hyphens with underscores for valid class names
  }
  // Fallback: just clean up the string
  return cssVar.replace(/[^a-zA-Z0-9_-]/g, '_');
};

export const renderD3TimeSeriesChart = (
  svgElement: SVGSVGElement | null,
  data: TimeSeriesDataPoint[],
  config: D3ChartConfig,
  chartTimeScale: string
): void => {
  if (!svgElement || !data || data.length === 0) {
    if (svgElement) d3.select(svgElement).selectAll('*').remove();
    return;
  }

  const chartData = data.filter((p): p is TimeSeriesDataPoint => {
    // Type guard to ensure p has the property defined by config.dataKey
    if (config.dataKey === 'layEV' || config.dataKey === 'midpointEV') {
      return (
        config.dataKey in p && (p as EVHistoryEntry)[config.dataKey] !== null
      );
    } else if (config.dataKey === 'total' || config.dataKey === 'rating') {
      return (
        config.dataKey in p &&
        (p as LiquidityHistoryEntry)[config.dataKey] !== null
      );
    } else if (config.dataKey === 'adjustedEV') {
      return (
        config.dataKey in p &&
        (p as BackOddsHistoryEntry)[config.dataKey] !== null
      );
    }
    return false;
  });

  if (chartData.length === 0) {
    d3.select(svgElement).selectAll('*').remove();
    return;
  }

  try {
    d3.select(svgElement).selectAll('*').remove();

    const margin = { top: 20, right: 20, bottom: 30, left: 40 };
    const width = svgElement.clientWidth - margin.left - margin.right;
    const height = svgElement.clientHeight - margin.top - margin.bottom;

    if (width <= 0 || height <= 0) return;

    const svg = d3
      .select(svgElement)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    const x = d3
      .scaleTime()
      .domain(
        d3.extent(chartData, (d) => new Date(d.timestamp)) as [Date, Date]
      )
      .range([0, width]);

    // Helper function to get the value based on dataKey with type safety
    const getYValue = (d: TimeSeriesDataPoint): number | null => {
      if (config.dataKey === 'layEV' && 'layEV' in d) return d.layEV;
      if (config.dataKey === 'midpointEV' && 'midpointEV' in d)
        return d.midpointEV;
      if (config.dataKey === 'total' && 'total' in d) return d.total;
      if (config.dataKey === 'rating' && 'rating' in d) return d.rating;
      if (config.dataKey === 'adjustedEV' && 'adjustedEV' in d)
        return d.adjustedEV;
      return null;
    };

    const yValues = chartData
      .map(getYValue)
      .filter((v) => v !== null) as number[];
    const yMin = d3.min(yValues) ?? 0;
    const yMax = d3.max(yValues) ?? 0;
    const yRange = yMax - yMin;
    let yBuffer: number;
    if (config.dataKey === 'total') {
      yBuffer = Math.max(yRange * 0.2, 100);
    } else {
      yBuffer = Math.max(yRange * 0.2, 10);
    }
    const y = d3
      .scaleLinear()
      .domain([yMin - yBuffer, yMax + yBuffer])
      .range([height, 0])
      .nice();

    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(5)
          .tickFormat(() => '')
      )
      .attr('class', 'd3-chart-axis');
    const yAxis = svg
      .append('g')
      .call(
        d3
          .axisLeft(y)
          .ticks(5)
          .tickFormat(
            config.yAxisFormatter ||
              ((d_1) =>
                d3.format('.0f')(d_1 as number) +
                (config.dataKey !== 'total' ? '%' : ''))
          )
      )
      .attr('class', 'd3-chart-axis');
    yAxis
      .selectAll('text')
      .attr('class', 'y-label')
      .style('font-size', '10px')
      .style('fill', 'var(--text-secondary)');
    svg
      .selectAll('.grid-line')
      .data(y.ticks(5))
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d_2) => y(d_2 as number))
      .attr('y2', (d_3) => y(d_3 as number))
      .style('stroke', 'var(--border)')
      .style('stroke-opacity', 0.3);

    const line = d3
      .line<TimeSeriesDataPoint>()
      .x((d_4) => x(new Date(d_4.timestamp)))
      .y((d_5) => {
        const val = getYValue(d_5);
        return val !== null ? y(val) : y(0); // Fallback to y(0) if val is null, though `defined` should handle this
      })
      .defined((d_6) => getYValue(d_6) !== null)
      .curve(d3.curveCatmullRom.alpha(0.5));

    svg
      .append('path')
      .datum(chartData)
      .attr('class', `data-line ${cssVarToClassName(config.lineColorVar)}-line`)
      .style('stroke', config.lineColorVar)
      .attr('d', line);
    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'd3-tooltip')
      .style('opacity', 0);

    svg
      .selectAll(`.data-point.${cssVarToClassName(config.pointFillVar)}-point`)
      .data(chartData)
      .enter()
      .append('circle')
      .attr(
        'class',
        `data-point ${cssVarToClassName(config.pointFillVar)}-point`
      )
      .style('fill', config.pointFillVar)
      .attr('cx', (d_7) => x(new Date(d_7.timestamp)))
      .attr('cy', (d_8) => {
        const val = getYValue(d_8);
        return val !== null ? y(val) : y(0); // Fallback
      })
      .attr('r', 1.5)
      .on('mouseover', function (event, d_9) {
        d3.select(this).transition().duration(100).attr('r', 3);
        tooltip
          .html(config.tooltipFormatter(d_9))
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY - 28}px`)
          .transition()
          .duration(200)
          .style('opacity', 1);
      })
      .on('mouseout', function () {
        d3.select(this).transition().duration(100).attr('r', 1.5);
        tooltip.transition().duration(200).style('opacity', 0).remove(); // Ensure tooltip is removed
      });

    if (chartData.length > 0) {
      const firstPoint = chartData[0];
      const firstTimestamp = new Date(firstPoint.timestamp);
      const now = new Date();
      const elapsedMs = now.getTime() - firstTimestamp.getTime();
      const elapsedHours = Math.floor(elapsedMs / 3600000);
      const elapsedMinutes = Math.floor((elapsedMs % 3600000) / 60000);
      const elapsedText =
        elapsedHours > 0
          ? `${elapsedHours}h ${elapsedMinutes}m ago`
          : `${elapsedMinutes}m ago`;
      const timeScaleText =
        chartTimeScale === 'minute'
          ? '1-min intervals'
          : `${chartTimeScale.replace('minute', 'min')} intervals`;
      svg
        .append('text')
        .attr('class', 'time-scale-indicator')
        .attr('text-anchor', 'middle')
        .attr('x', width / 2)
        .attr('y', height + margin.bottom - 5)
        .text(timeScaleText);
      svg
        .append('text')
        .attr('class', 'first-data-timestamp')
        .attr('text-anchor', 'start')
        .attr('x', 0)
        .attr('y', -5)
        .text(`Started: ${elapsedText}`);
    }
  } catch (error) {
    console.error(
      `Error rendering D3 chart with key ${String(config.dataKey)}:`,
      error
    );
    if (svgElement) d3.select(svgElement).selectAll('*').remove();
  }
};
